import type Phaser from "phaser";
import { MODAL_EVENT } from "@yjsgames/phaser-ui";
import { DepthBands, ViewStack } from "@yjsgames/phaser-ui/view-order";
import { UI_BANDS } from "../objects/depthLayers.js";
import { SettingsModal } from "./SettingsModal.js";
import { AccountLinkModal } from "./AccountLinkModal.js";
import { ConfirmPopup } from "./ConfirmPopup.js";
import type { AccountStatus } from "@yjsgames/account-link";
import { CheatPanel } from "@yjsgames/phaser-cheat";
import { DebugDashboardModal } from "./DebugDashboardModal.js";
import { RewardAcquisitionModal, type RewardCellModel } from "./RewardAcquisitionModal.js";
import { RewardDetailModal } from "./RewardDetailModal.js";
import { RelicDetailPopup, type RelicDetailData } from "./RelicDetailPopup.js";
import { ModuleInfoPopup } from "./ModuleInfoPopup.js";
import { SynergyInfoPopup, type SynergyInfoData } from "./SynergyInfoPopup.js";
import { SpaceshipInfoPopup, type SpaceshipInfoData } from "./SpaceshipInfoPopup.js";
import { GridUnlockConfirmPopup } from "./GridUnlockConfirmPopup.js";
import { RebirthConfirmPopup } from "./RebirthConfirmPopup.js";
import { UpgradeConfirmPopup } from "./UpgradeConfirmPopup.js";
import { GoldBoxOddsPopup } from "./GoldBoxOddsPopup.js";
import { PurchaseConfirmPopup } from "./PurchaseConfirmPopup.js";
import { WorldMoveConfirmPopup } from "./WorldMoveConfirmPopup.js";
import { BlockingLoadingOverlay } from "./BlockingLoadingOverlay.js";
import type { ModuleInfoData } from "./tabData.js";
import type { LanguageCode } from "../i18n/locales.js";
import type { NumberFormat } from "../models/ModuleBrickSaveData.js";
import { HouseAdOverlay } from "../ads/HouseAdOverlay.js";
import type { HouseAdModalRequest } from "@yjsgames/ad-ports";
import type { HouseAdCampaign } from "@yjsgames/house-ads";
import { t } from "../i18n/i18n.js";

/** add.existing + open/close + depth 부여가 가능한 모달(Phaser Container 상속 — ViewStack StackableView 충족). */
type Modal = Phaser.GameObjects.GameObject & {
  open(): void;
  close(): void;
  setDepth(depth: number): unknown;
};

/** 오버레이 depth 밴드 — 상수 SSoT는 depthLayers.UI_BANDS, 겹침 검증·계산은 패키지. */
const BANDS = new DepthBands(UI_BANDS);

type SettingsOptions = ConstructorParameters<typeof SettingsModal>[1];
type AccountLinkOptions = ConstructorParameters<typeof AccountLinkModal>[1];
type ConfirmOptions = ConstructorParameters<typeof ConfirmPopup>[1];
type CheatOptions = ConstructorParameters<typeof CheatPanel>[1];
type DebugDashboardOptions = ConstructorParameters<typeof DebugDashboardModal>[1];
type RewardOptions = ConstructorParameters<typeof RewardAcquisitionModal>[1];
type RewardDetailOptions = ConstructorParameters<typeof RewardDetailModal>[1];
type RelicDetailOptions = ConstructorParameters<typeof RelicDetailPopup>[1];
type ModuleInfoOptions = ConstructorParameters<typeof ModuleInfoPopup>[1];
type SynergyInfoOptions = ConstructorParameters<typeof SynergyInfoPopup>[1];
type SpaceshipInfoOptions = ConstructorParameters<typeof SpaceshipInfoPopup>[1];
type GridUnlockConfirmOptions = ConstructorParameters<typeof GridUnlockConfirmPopup>[1];
type UpgradeConfirmOptions = ConstructorParameters<typeof UpgradeConfirmPopup>[1];
type RebirthConfirmOptions = ConstructorParameters<typeof RebirthConfirmPopup>[1];
type GoldBoxOddsOptions = ConstructorParameters<typeof GoldBoxOddsPopup>[1];
type PurchaseConfirmOptions = ConstructorParameters<typeof PurchaseConfirmPopup>[1];
type WorldMoveConfirmOptions = ConstructorParameters<typeof WorldMoveConfirmPopup>[1];

/**
 * 모달/팝업 생명주기 호스트 — GameScene에서 추출(god-object 분해, "팝업 단위" 분리).
 * Settings/Cheat/DebugDashboard/RewardAcquisition/RewardDetail/RelicDetail/ModuleInfo/
 * GridUnlockConfirm(Phaser 모달) + HouseAd(DOM 오버레이)의 인스턴스 보관 + 생성(add.existing+open)
 * + 닫기(close+null) + 계층/전환 관계를 한 곳에 캡슐화한다.
 * 도메인 콜백(onSetChapter 등)은 GameScene이 options로 주입 — 본 호스트는 표시/소멸만 책임진다.
 */
export class ModalHost {
  private settings: SettingsModal | null = null;
  private accountLink: AccountLinkModal | null = null;
  private confirm: ConfirmPopup | null = null;
  private cheat: CheatPanel | null = null;
  private debugDashboard: DebugDashboardModal | null = null;
  private reward: RewardAcquisitionModal | null = null;
  private rewardDetail: RewardDetailModal | null = null;
  private relicDetail: RelicDetailPopup | null = null;
  private moduleInfo: ModuleInfoPopup | null = null;
  private synergyInfo: SynergyInfoPopup | null = null;
  private spaceshipInfo: SpaceshipInfoPopup | null = null;
  private gridUnlockConfirm: GridUnlockConfirmPopup | null = null;
  private upgradeConfirm: UpgradeConfirmPopup | null = null;
  private rebirthConfirm: RebirthConfirmPopup | null = null;
  private goldBoxOdds: GoldBoxOddsPopup | null = null;
  private purchaseConfirm: PurchaseConfirmPopup | null = null;
  private worldMoveConfirm: WorldMoveConfirmPopup | null = null;
  private houseAd: HouseAdOverlay | null = null;
  private blockingLoading: BlockingLoadingOverlay | null = null;
  /** 광고 음소거 상태 — 직전 광고에서 조절한 값을 다음 광고가 이어받도록 세션 동안 유지(첫 광고는 자동재생 위해 muted). */
  private houseAdMuted = true;

  /**
   * 모달 뷰 order 스택 — push 순서대로 modal 밴드 depth(2000, 2010, …)를 자동 부여해
   * "나중에 연 팝업이 위" 상대 순서를 add 순서가 아닌 명시 depth로 보장한다 (reward→rewardDetail 2단).
   * 스택 제거는 MODAL_EVENT.Closed 이벤트 경로 — close()가 비동기(퇴장 tween 후 destroy)라
   * 직접 제거하면 닫히는 중 모달과 스택이 어긋난다. remove는 idempotent라 중복 호출 안전.
   * (HouseAdOverlay는 DOM 오버레이 — Phaser depth 무관이라 스택 제외.)
   */
  private readonly stack = new ViewStack<Modal>({ depthFor: (i) => BANDS.depthFor("modal", i) });

  /**
   * 모달 열림/닫힘 사운드 콜백 (DIP — ModalHost는 사운드에 의존하지 않고 콜백만 호출).
   * GameScene이 lazy 참조(`() => controllers.soundController.playUi(...)`)로 주입한다 — ModalHost가
   * controllers 생성 전에 만들어지므로 직접 참조가 아닌 콜백이어야 한다. 미주입 시 무음(옵션).
   */
  constructor(
    private readonly scene: Phaser.Scene,
    private readonly sound?: { onOpen(): void; onClose(): void },
    /**
     * 모달 stack이 완전히 비는 순간(마지막 모달 close 완료) 1회 호출 — 모달 중 isAnyModalOpen()로
     * 스킵됐던 탭 refresh를 복구한다. 특히 battle 그리드는 250ms 타이머가 제외(드래그 보호)하므로
     * 모달 내 mutation(장착 해제 등)이 모달 close 후 갱신되지 않던 잔류 버그를 막는다. (중첩 모달은 완전 비움 시에만.)
     */
    private readonly onAllClosed?: () => void,
  ) {}

  /** 공통 표시 — add.existing + 스택 push(depth 자동 부여) + open. 모든 모달이 같은 표시 절차를 공유한다. */
  private present<T extends Modal>(modal: T): T {
    this.scene.add.existing(modal);
    this.stack.push(modal);
    modal.once(MODAL_EVENT.Closed, () => {
      this.stack.remove(modal);
      this.sound?.onClose();
      // 마지막 모달이 닫혀 stack이 비면 — 모달 중 스킵된 탭 refresh(특히 battle) 복구.
      if (this.stack.size === 0) this.onAllClosed?.();
    });
    modal.open();
    this.sound?.onOpen();
    return modal;
  }

  /**
   * 풀스크린/dim 모달(stack 등록분)이 하나라도 열려있는가 — 열려있으면 뒤의 탭 UI가 가려져
   * refresh가 무의미하다. GameScene이 모달 중 탭 refresh를 스킵해 모달 위로 게임 렉이 전파되는 것을 막는다.
   * (HouseAd는 DOM 오버레이라 stack 제외 — 미포함.)
   */
  isAnyModalOpen(): boolean {
    return this.stack.size > 0;
  }

  // ── 설정 (톱니) — Cheat/DebugDashboard와 동일 레이어(서로 전환) ──
  get settingsOpen(): boolean {
    return this.settings !== null;
  }
  openSettings(opts: SettingsOptions): void {
    this.closeSettings();
    const modal = new SettingsModal(this.scene, opts);
    this.settings = modal;
    this.present(modal);
  }
  closeSettings(): void {
    this.settings?.close();
    this.settings = null;
  }
  applyLanguageToSettings(code: LanguageCode): void {
    this.settings?.applyLanguage(code);
  }
  setSettingsNumberFormat(format: NumberFormat): void {
    this.settings?.setNumberFormat(format);
  }
  // ── 계정 연동 및 삭제 모달 (설정 위에 스택 — 연동 섹션[네이티브] + 삭제 버튼) ──
  openAccountLink(opts: AccountLinkOptions): void {
    this.closeAccountLink();
    this.accountLink = this.present(new AccountLinkModal(this.scene, opts));
  }
  closeAccountLink(): void {
    this.accountLink?.close();
    this.accountLink = null;
  }
  /** 연동 성공 후 모달의 상태/버튼을 인플레이스 갱신(열려 있을 때만). */
  updateAccountLinkStatus(status: AccountStatus): void {
    this.accountLink?.setStatus(status);
  }

  setAccountLinkConnecting(value: boolean): void {
    this.accountLink?.setConnecting(value);
  }

  setSettingsAccountNeedsLink(value: boolean): void {
    this.settings?.setAccountNeedsLink(value);
  }

  // ── 범용 확인(yes/no) 팝업 — 계정 삭제 등 파괴적 액션 확인 (계정 모달 위에 스택) ──
  openConfirm(opts: ConfirmOptions): void {
    this.closeConfirm();
    this.confirm = this.present(new ConfirmPopup(this.scene, opts));
  }
  closeConfirm(): void {
    this.confirm?.close();
    this.confirm = null;
  }

  openCheat(opts: CheatOptions): void {
    this.closeCheat();
    this.closeSettings();
    this.cheat = this.present(new CheatPanel(this.scene, opts));
  }
  get cheatOpen(): boolean {
    return this.cheat !== null;
  }
  closeCheat(): void {
    this.cheat?.close();
    this.cheat = null;
  }

  openDebugDashboard(opts: DebugDashboardOptions): void {
    this.closeSettings();
    this.debugDashboard = this.present(new DebugDashboardModal(this.scene, opts));
  }
  closeDebugDashboard(): void {
    this.debugDashboard?.close();
    this.debugDashboard = null;
  }

  // ── 보상 획득 — reward 위에 rewardDetail이 쌓이는 2단 계층 ──
  hasReward(): boolean {
    return this.reward !== null;
  }
  setRewardCells(cells: RewardCellModel[]): void {
    this.reward?.setCells(cells);
  }
  openReward(opts: RewardOptions): void {
    this.reward = this.present(new RewardAcquisitionModal(this.scene, opts));
  }
  closeReward(): void {
    this.closeRewardDetail(); // 위에 쌓인 상세부터 닫는다
    this.reward?.close();
    this.reward = null;
  }
  openRewardDetail(opts: RewardDetailOptions): void {
    this.rewardDetail?.close();
    this.rewardDetail = this.present(new RewardDetailModal(this.scene, opts));
  }
  closeRewardDetail(): void {
    this.rewardDetail?.close();
    this.rewardDetail = null;
  }

  // ── 유물 상세 (독립 팝업) ──
  openRelicDetail(opts: RelicDetailOptions): void {
    this.closeRelicDetail();
    this.relicDetail = this.present(new RelicDetailPopup(this.scene, opts));
  }
  closeRelicDetail(): void {
    this.relicDetail?.close();
    this.relicDetail = null;
  }
  updateRelicDetail(data: RelicDetailData): void {
    this.relicDetail?.update(data);
  }

  // ── 모듈 정보 (독립 팝업 — 합성 결과 + 전투 그리드 탭 공용) ──
  openModuleInfo(opts: ModuleInfoOptions): void {
    this.closeModuleInfo();
    this.moduleInfo = this.present(new ModuleInfoPopup(this.scene, opts));
  }
  closeModuleInfo(): void {
    this.moduleInfo?.close();
    this.moduleInfo = null;
  }
  updateModuleInfo(data: ModuleInfoData): void {
    this.moduleInfo?.update(data);
  }
  applyLanguageToModuleInfo(): void {
    this.moduleInfo?.applyLanguage();
  }

  // ── 시너지 정보 (독립 팝업 — 도감/전투탭 공용) ──
  openSynergyInfo(opts: SynergyInfoOptions): void {
    this.closeSynergyInfo();
    this.synergyInfo = this.present(new SynergyInfoPopup(this.scene, opts));
  }
  closeSynergyInfo(): void {
    this.synergyInfo?.close();
    this.synergyInfo = null;
  }
  updateSynergyInfo(data: SynergyInfoData): void {
    this.synergyInfo?.update(data);
  }
  applyLanguageToSynergyInfo(): void {
    this.synergyInfo?.applyLanguage();
  }

  // ── 우주선 정보 (독립 팝업 — 우주선 탭 목록 카드) ──
  openSpaceshipInfo(opts: SpaceshipInfoOptions): void {
    this.closeSpaceshipInfo();
    this.spaceshipInfo = this.present(new SpaceshipInfoPopup(this.scene, opts));
  }
  closeSpaceshipInfo(): void {
    this.spaceshipInfo?.close();
    this.spaceshipInfo = null;
  }
  updateSpaceshipInfo(data: SpaceshipInfoData): void {
    this.spaceshipInfo?.update(data);
  }

  // ── 그리드 잠금 칸 해제 확인 (독립 팝업 — 전투 그리드 탭) ──
  openGridUnlockConfirm(opts: GridUnlockConfirmOptions): void {
    this.closeGridUnlockConfirm();
    this.gridUnlockConfirm = this.present(new GridUnlockConfirmPopup(this.scene, opts));
  }
  closeGridUnlockConfirm(): void {
    this.gridUnlockConfirm?.close();
    this.gridUnlockConfirm = null;
  }

  // ── 모듈 영구 강화 확인 (독립 팝업 — 파이프 정보 팝업에서 호출) ──
  openUpgradeConfirm(opts: UpgradeConfirmOptions): void {
    this.closeUpgradeConfirm();
    this.upgradeConfirm = this.present(new UpgradeConfirmPopup(this.scene, opts));
  }
  closeUpgradeConfirm(): void {
    this.upgradeConfirm?.close();
    this.upgradeConfirm = null;
  }

  // ── 환생 확인 (독립 팝업 — 환생 탭) ──
  openRebirthConfirm(opts: RebirthConfirmOptions): void {
    this.closeRebirthConfirm();
    this.rebirthConfirm = this.present(new RebirthConfirmPopup(this.scene, opts));
  }
  closeRebirthConfirm(): void {
    this.rebirthConfirm?.close();
    this.rebirthConfirm = null;
  }

  // ── 월드 이동 확인 (독립 팝업 — 월드 탭) ──
  openWorldMoveConfirm(opts: WorldMoveConfirmOptions): void {
    this.closeWorldMoveConfirm();
    this.worldMoveConfirm = this.present(new WorldMoveConfirmPopup(this.scene, opts));
  }
  closeWorldMoveConfirm(): void {
    this.worldMoveConfirm?.close();
    this.worldMoveConfirm = null;
  }

  // ── 골드 상자 확률(?) 팝업 (독립 팝업 — 상점 탭) ──
  openGoldBoxOdds(opts: GoldBoxOddsOptions): void {
    this.closeGoldBoxOdds();
    this.goldBoxOdds = this.present(new GoldBoxOddsPopup(this.scene, opts));
  }
  closeGoldBoxOdds(): void {
    this.goldBoxOdds?.close();
    this.goldBoxOdds = null;
  }

  // ── 구매 확인 (독립 팝업 — 상점 재화 소비 구매) ──
  openPurchaseConfirm(opts: PurchaseConfirmOptions): void {
    this.closePurchaseConfirm();
    this.purchaseConfirm = this.present(new PurchaseConfirmPopup(this.scene, opts));
  }
  closePurchaseConfirm(): void {
    this.purchaseConfirm?.close();
    this.purchaseConfirm = null;
  }

  // ── 보상형 하우스 광고 (최상위 레이어 — 일일상점 광고 코스트 시청) ──
  openHouseAd(req: HouseAdModalRequest<HouseAdCampaign>): void {
    this.closeHouseAd();
    this.houseAd = new HouseAdOverlay({
      adType: req.campaign.adType,
      mediaUrl: req.mediaUrl,
      format: req.format,
      mediaVolume: req.campaign.volume,
      mutedInitial: this.houseAdMuted,
      onMutedChange: (muted) => {
        this.houseAdMuted = muted;
      },
      titleText: t(req.campaign.titleKey),
      descText: req.campaign.descKey ? t(req.campaign.descKey) : undefined,
      storeText: t(req.campaign.ctaKey),
      closeText: t("houseAd.close"),
      countdownLabel: (sec) => t("houseAd.closeIn", { sec }),
      onClose: req.onClose,
      onCtaClick: req.onCtaClick,
    });
  }
  closeHouseAd(): void {
    this.houseAd?.destroy();
    this.houseAd = null;
  }

  // ── 외부 SDK/스토어 대기 overlay (광고/인앱결제 결과 콜백까지 입력 차단) ──
  private openBlockingLoading(): void {
    if (this.blockingLoading) return;
    this.blockingLoading = this.present(
      new BlockingLoadingOverlay(this.scene, {
        screenWidth: this.scene.scale.width,
        screenHeight: this.scene.scale.height,
      }),
    );
  }

  private closeBlockingLoading(): void {
    this.blockingLoading?.close();
    this.blockingLoading = null;
  }

  openAdLoading(): void {
    this.openBlockingLoading();
  }

  closeAdLoading(): void {
    this.closeBlockingLoading();
  }

  openStoreLoading(): void {
    this.openBlockingLoading();
  }

  closeStoreLoading(): void {
    this.closeBlockingLoading();
  }

  /** 탭 전환 시 — 톱니 모달 + 유물/모듈 정보 팝업을 닫는다(탭과 독립 레이어 정리). */
  closeOverlays(): void {
    this.closeSettings();
    this.closeRelicDetail();
    this.closeModuleInfo();
    this.closeSynergyInfo();
    this.closeSpaceshipInfo();
    this.closeGridUnlockConfirm();
    this.closeUpgradeConfirm();
    this.closeRebirthConfirm();
    this.closeWorldMoveConfirm();
    this.closeGoldBoxOdds();
    this.closePurchaseConfirm();
    this.closeBlockingLoading();
  }
}
